import { useSocket } from '@/app/providers/socket-provider';
import { CountUnreadNotificationsDocument } from '@/graphql/generated/count-unread-notifications.generated';
import { useLazyQuery } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { instantDownload } from '@/utils/instant-download';
import {
    NotificationButton,
    NotificationTab,
    PostMessageChannel,
} from '@/graphql/generated/types.generated';
import { NotificationDocument, NotificationQuery } from '@/graphql/generated/notification.generated';
import { getOpenThread } from '@/components/post-planning/post/post-planner/communication/open-thread';

// Redirect target of a post notification, e.g. "/posts/calendar/post/<encryptedId>" (see the backend
// POST_PREVIEW redirect) — the encrypted post id is the last segment.
const POST_PATH_PREFIX = '/posts/calendar/post/';

// A mention always breaks through — even in the open thread, being @mentioned is worth a toast.
const MENTION_MESSAGE_KEY = 'notification.post-message-mention';

// Which chat channel each thread-message notification belongs to (mentions are handled separately).
const CHANNEL_BY_MESSAGE_KEY: Record<string, PostMessageChannel> = {
    'notification.post-message-internal': PostMessageChannel.Internal,
    // Both directions of the client conversation live on the same channel, and the toast is
    // suppressed by which thread is open — not by who wrote — so both map to it.
    'notification.post-message-from-client': PostMessageChannel.Client,
    'notification.post-message-to-client': PostMessageChannel.Client,
};

const NotificationsEmitControl = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [countNotifications] = useLazyQuery(CountUnreadNotificationsDocument, {
        fetchPolicy: 'network-only',
    });
    const [notification] = useLazyQuery(NotificationDocument);
    const { socket } = useSocket();
    const [notifications, setNotifications] = useState<NotificationQuery['notification'][]>([]);
    const [notify, setNotify] = useState(false);

    const handleNotifications = (notification: NotificationQuery['notification']) => {
        setNotifications((notifications) => {
            const clone = notifications.slice();

            const sameGroup = notifications.findIndex(
                (each) =>
                    (each.single || each.stacked)?.group ===
                    (notification.single || notification.stacked)?.group,
            );
            if (sameGroup !== -1) {
                clone.splice(sameGroup, 1);
            }

            clone.push(notification);

            return clone;
        });
    };

    useEffect(() => {
        if (socket) {
            socket.on('notification', async ({ notificationId }) => {
                setNotify(false);
                const { data } = await notification({ variables: { id: notificationId } });
                if (data?.notification) {
                    handleNotifications(data.notification);
                }
                await new Promise((resolve) => setTimeout(resolve, 5000));
                setNotify(true);
            });

            // Clean up the connection when the component is unmounted
            return () => {
                socket.off('notification');
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket]);

    // Don't pop a toast for a message on the exact thread (post + channel) the user is watching live — a
    // mention is the exception and always toasts. Matching the open thread (not just the URL) means a
    // message on the OTHER channel of the same post still toasts, and it works in any post view. The bell
    // still records everything.
    const suppressToast = (single: NonNullable<NotificationQuery['notification']['single']>) => {
        if (single.message === MENTION_MESSAGE_KEY) {
            return false;
        }
        const channel = CHANNEL_BY_MESSAGE_KEY[single.message];
        if (!channel) {
            return false;
        }
        const open = getOpenThread();
        if (!open || open.channel !== channel) {
            return false;
        }
        const redirect = single.actions?.find(
            (action) =>
                action.type === NotificationButton.Redirect &&
                typeof action.data === 'string' &&
                action.data.startsWith(POST_PATH_PREFIX),
        );
        if (!redirect) {
            return false;
        }
        const postId = (redirect.data as string).slice(POST_PATH_PREFIX.length).split('/')[0];
        return postId === open.postId;
    };

    useEffect(() => {
        if (notify) {
            countNotifications();
            for (const notification of notifications) {
                if (!(notification.single && suppressToast(notification.single))) {
                    enqueueSnackbar({
                        ...(notification.single && {
                            variant: 'singleNotification',
                            single: {
                                ...notification.single,
                                tab: NotificationTab.All,
                            },
                        }),
                        ...(notification.stacked && {
                            variant: 'stackedNotification',
                            stacked: notification.stacked,
                        }),
                        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
                    });
                }

                if (notification.single) {
                    const { actions } = notification.single;
                    if (actions) {
                        const [primaryButton, secondaryButton] = actions;
                        if (primaryButton?.type === NotificationButton.InstantDownload) {
                            instantDownload(primaryButton.data);
                        }

                        if (secondaryButton?.type === NotificationButton.InstantDownload) {
                            instantDownload(secondaryButton.data);
                        }
                    }
                }
            }
            setNotifications([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notify]);

    return null;
};

export default NotificationsEmitControl;
